import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Heading from 'components/shared/heading';
import Link from 'components/shared/link';
import styles from './styles.module.scss';

const DEFAULT_MAX_IMAGE_WIDTH = 160;

const parsePx = (value, { min, fallback = null } = {}) => {
  const satisfiesMin = (n) => min === undefined || n >= min;

  if (typeof value === 'number' && Number.isFinite(value) && satisfiesMin(value)) {
    return Math.round(value);
  }

  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const match = String(value).match(/(-?\d+(?:\.\d+)?)/);
  if (!match) return fallback;

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && satisfiesMin(parsed) ? Math.round(parsed) : fallback;
};

const parseMaxWidthPx = (value, fallback = DEFAULT_MAX_IMAGE_WIDTH) =>
  parsePx(value, { min: 1, fallback });

const parseSpacingPx = (value) => parsePx(value, { fallback: null });

const getVisibleChildren = (children) =>
  React.Children.toArray(children).filter((child) => {
    if (typeof child === 'string') return child.trim().length > 0;
    return Boolean(child);
  });

const Logos = ({
  title = null,
  description = null,
  items = [],
  alignment = 'center',
  withSeparator = false,
  maxImageWidth = DEFAULT_MAX_IMAGE_WIDTH,
  setTop = null,
  setBottom = null,
  theme = '',
  children = null,
}) => {
  // Images flow into the block as nested HTML from WordPress and are converted to
  // React elements (Gatsby-optimized image wrappers) by the lazy-blocks parser.
  // We just need to render them — no DOM injection / ref dance required.
  const visibleChildren = getVisibleChildren(children);
  if (visibleChildren.length === 0) return null;

  const maxWidthPx = parseMaxWidthPx(maxImageWidth, DEFAULT_MAX_IMAGE_WIDTH);
  const itemsList = Array.isArray(items) ? items : [];
  const marginTopPx = parseSpacingPx(setTop);
  const marginBottomPx = parseSpacingPx(setBottom);

  const sectionStyle =
    marginTopPx !== null || marginBottomPx !== null
      ? {
          ...(marginTopPx !== null && { marginTop: `${marginTopPx}px` }),
          ...(marginBottomPx !== null && { marginBottom: `${marginBottomPx}px` }),
        }
      : undefined;

  return (
    <section
      className={cn(styles.logos, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
      style={sectionStyle}
    >
      <div className="container">
        {title && (
          <Heading className={styles.title} tag="h2" size="md">
            {title}
          </Heading>
        )}
        {description && (
          <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
        )}
        <div
          className={cn(styles.list, {
            [styles.alignLeft]: alignment === 'left',
            [styles.alignRight]: alignment === 'right',
            [styles.alignCenter]: alignment === 'center' || !alignment,
            [styles.withSeparator]: withSeparator,
          })}
        >
          {visibleChildren.map((child, idx) => {
            const item = itemsList[idx] || {};
            const link = typeof item.link === 'string' && item.link.length > 0 ? item.link : null;
            const ariaLabel = typeof item.alt === 'string' && item.alt.length > 0 ? item.alt : null;

            const slot = (
              <span className={styles.logoSlot} style={{ maxWidth: `${maxWidthPx}px` }}>
                {child}
              </span>
            );

            const content = link ? (
              <Link to={link} className={styles.logoLink} aria-label={ariaLabel || undefined}>
                {slot}
              </Link>
            ) : (
              slot
            );

            return (
              <React.Fragment key={idx}>
                {idx > 0 && withSeparator && (
                  <span className={styles.separator} aria-hidden="true" />
                )}
                {content}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

Logos.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      alt: PropTypes.string,
    })
  ),
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  withSeparator: PropTypes.bool,
  maxImageWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setBottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  children: PropTypes.node,
};

export default Logos;
